import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { parseGid, useCart } from "@shopify/hydrogen-react";
import {
  type Maybe,
  type ProductVariant,
} from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import { loopAPIFetcher } from "@react/hooks/fetchers";
import { useCartAnalytics } from "@react/hooks/useCartAnalytics";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { LOOP_RETURNS_API_URL } from "@root/tablescape/constants";
import { type CartSectionSettings } from "@root/tablescape/state";

import { CartCheckoutView } from "./CartCheckout.view";

const CartCheckoutBase = () => {
  const [trackingApplied, setTrackingApplied] = useState(false);
  const [loopReturnsRedirectEnabled, setLoopReturnsRedirectEnabled] =
    useState(false);

  const { checkoutUrl, cost, id: cartId, lines, status } = useCart();
  const { reportContinueToCheckoutEvent } = useCartAnalytics();

  const {
    content: {
      baseDrawer: { checkoutCtaText },
    },
    meta: { customer },
  } = useSectionSettings<CartSectionSettings>();

  const loopOnstoreData = useMemo<
    Maybe<{
      active: boolean;
      api: string | null;
      debug: boolean;
      draftOrderId: string | null;
      fullPayload: boolean;
      id: string;
      key: string;
      params: Maybe<{
        loop_base: number | null;
        loop_credit: number | null;
        loop_currency: string | null;
        loop_customer_name: string | null;
        loop_discount_percentage: number | null;
        loop_domain: string | null;
        loop_redirect_url: string | null;
        loop_return_id: string | null;
        loop_subdomain: string | null;
        loop_total: number | null;
      }>;
      preserveCart: boolean;
      redirect: boolean;
      testMode: boolean;
      token: string | null;
    }>
  >(() => {
    try {
      return JSON.parse(window.localStorage["loop-onstore-data"] ?? "null");
    } catch (e) {}

    return null;
  }, []);

  const variantIds = useMemo(
    () =>
      lines
        ?.map((line) => parseGid(line?.merchandise?.id)?.id)
        .filter((id): id is string => !!id),
    [lines]
  );

  const { data: loopCartData, error: loopCartError } = useSWR<{
    data: {
      cart: ProductVariant["id"][];
    };
    token: string;
  }>(
    loopOnstoreData?.params?.loop_domain
      ? [LOOP_RETURNS_API_URL, variantIds]
      : null,
    loopAPIFetcher
  );

  const handleCheckout = useCallback(() => {
    if (loopOnstoreData?.params?.loop_domain && !loopCartError) {
      setLoopReturnsRedirectEnabled(true);
      return;
    }

    reportContinueToCheckoutEvent({
      cost,
      checkoutUrl,
      customer,
      id: cartId,
      lines,
    });

    setTrackingApplied(true);
  }, [
    cartId,
    checkoutUrl,
    cost,
    customer,
    lines,
    loopCartError,
    loopOnstoreData,
    reportContinueToCheckoutEvent,
  ]);

  useEffect(() => {
    if (
      !loopCartError &&
      loopReturnsRedirectEnabled &&
      loopOnstoreData?.params?.loop_domain &&
      loopCartData?.token
    ) {
      const loopUrl = `${location.protocol}//${loopOnstoreData.params.loop_domain}/#/cart/v2/${loopCartData.token}`;
      window.location.href = loopUrl;
    }
  }, [
    handleCheckout,
    loopReturnsRedirectEnabled,
    loopCartData?.token,
    loopCartError,
    loopOnstoreData?.params?.loop_domain,
  ]);

  useEffect(() => {
    if (trackingApplied && checkoutUrl && status === "idle") {
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl, status, trackingApplied]);

  return (
    <CartCheckoutView
      checkoutCtaText={checkoutCtaText}
      handleCheckout={handleCheckout}
    />
  );
};

export const CartCheckout = memo(CartCheckoutBase);
